/**
 * Cloud repository — reads via the public server functions defined in
 * `catalog.functions.ts` and maps DB rows onto the RC1 `CatalogProduct` shape
 * so the storefront UI does not need to change.
 *
 * The database is the only catalog source. No seeded image or collection
 * fallback is applied.
 */
import {
  type CatalogProduct,
  type CatalogFilters,
  type Collection,
} from "./products";
import {
  getProductBySlug as fnGetProductBySlug,
  listCategories as fnListCategories,
  listProductsByCategory as fnListProductsByCategory,
  listCollections as fnListCollections,
  listProductsByCollection as fnListProductsByCollection,
  searchAllProducts as fnSearchAllProducts,
} from "./catalog.functions";
import { applyCatalogFilters, type CatalogRepository } from "./repository";
import { mapDatabaseProduct, type DatabaseProductRow } from "./product-mapper";

/** RC1 fragrance catalog is Personal Luxury Perfumes in the new taxonomy. */
const FRAGRANCE_CATEGORY_ID = "personal-perfumes";

/**
 * Retries a server-function read once when the browser aborts the RPC request
 * itself (`TypeError: Failed to fetch`) — this happens when the connection is
 * dropped mid-flight, e.g. during a deploy/reload window. Application-level
 * errors are never retried, so data behaviour is unchanged.
 */
async function withNetworkRetry<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    const transient = err instanceof TypeError && /fetch/i.test(err.message);
    if (!transient) throw err;
    await new Promise((r) => setTimeout(r, 400));
    return run();
  }
}

export function cloudRepository(): CatalogRepository {
  async function loadAll(): Promise<CatalogProduct[]> {
    const res = await withNetworkRetry(() =>
      fnListProductsByCategory({
        data: { categoryId: FRAGRANCE_CATEGORY_ID, limit: 200 },
      }),
    );
    if (res.error) throw new Error(res.error);
    return (res.products as DatabaseProductRow[]).map(mapDatabaseProduct);
  }

  return {
    source: "cloud",
    async getProducts(filters?: Partial<CatalogFilters>) {
      const all = await loadAll();
      return applyCatalogFilters(all, filters);
    },
    async getProduct(slug: string) {
      const res = await fnGetProductBySlug({ data: { slug } });
      if (res.error) throw new Error(res.error);
      if (!res.product) return undefined;
      return mapDatabaseProduct(res.product as DatabaseProductRow);
    },
    async getCategories() {
      const res = await fnListCategories();
      if (res.error) throw new Error(res.error);
      return res.categories.map((c) => ({ id: c.id, slug: c.slug, name: c.name }));
    },
    async getCollections(): Promise<Collection[]> {
      const res = await withNetworkRetry(() => fnListCollections());
      if (res.error) throw new Error(res.error);
      return res.collections.map((c) => {
        const row = c as typeof c & {
          short_description?: string | null;
          long_description?: string | null;
          banner_image?: string | null;
          thumbnail_image?: string | null;
        };
        return {
          id: c.slug as Collection["id"],
          name: c.name,
          tagline: row.short_description ?? c.tagline ?? "",
          description: row.long_description ?? c.description ?? "",
          cover: row.banner_image ?? row.thumbnail_image ?? c.cover_image ?? "",
        };
      });
    },
    async searchProducts(query: string, opts?: { categoryId?: string; limit?: number }) {
      const data: { q: string; limit: number; categoryId?: string } = {
        q: query,
        limit: opts?.limit ?? 100,
      };
      if (opts?.categoryId) data.categoryId = opts.categoryId;
      const res = await fnSearchAllProducts({ data });
      if (res.error) throw new Error(res.error);
      return (res.products as DatabaseProductRow[]).map(mapDatabaseProduct);
    },
    async getProductsByCategory(categoryId: string, filters?: Partial<CatalogFilters>) {
      const res = await fnListProductsByCategory({ data: { categoryId, limit: 200 } });
      if (res.error) throw new Error(res.error);
      const all = (res.products as DatabaseProductRow[]).map(mapDatabaseProduct);
      return applyCatalogFilters(all, filters);
    },
    async getProductsByCollection(slug: string) {
      const res = await fnListProductsByCollection({ data: { slug, limit: 200 } });
      if (res.error) throw new Error(res.error);
      return (res.products as DatabaseProductRow[]).map(mapDatabaseProduct);
    },
  };
}